/**
 * Central SEO configuration for QnaRead (marketing + app route policy).
 * Set REACT_APP_SITE_URL in production (e.g. https://www.qnaread.com).
 */

import { QNAREAD } from './appLabels';
import { isPublicQuestionPath } from './routes';

const trimSlash = (url) => String(url || '').replace(/\/$/, '');

export const SITE_NAME = QNAREAD.name;
export const SITE_DOMAIN = 'QnaRead.com';

export const SITE_URL = trimSlash(
  process.env.REACT_APP_SITE_URL || 'https://www.qnaread.com',
);

export const DEFAULT_TITLE = `${QNAREAD.name} | ${QNAREAD.taglineShort}`;

export const DEFAULT_DESCRIPTION = `${QNAREAD.tagline} Search your library, run spaced repetition on Study hub, use AI explain on Pro, and share team notebooks.`;

export const DEFAULT_KEYWORDS =
  'QnaRead, Q&A learning, study app, spaced repetition, flashcards, interview prep, developer learning, team training, learning notebook, AI tutor, exam revision';

export const DEFAULT_OG_IMAGE = `${SITE_URL}/qnaread-logo.png`;

/** Social share image dimensions (logo asset; replace if you add public/og-image.png 1200×630). */
export const OG_IMAGE_WIDTH = 512;
export const OG_IMAGE_HEIGHT = 512;
export const OG_IMAGE_ALT = `${SITE_NAME} — structured Q&A learning platform`;

/** Optional: REACT_APP_TWITTER_HANDLE without @ (e.g. qnaread) */
export const TWITTER_HANDLE = (process.env.REACT_APP_TWITTER_HANDLE || '').replace(/^@/, '');

export const SITE_LANGUAGE = 'en';
export const SITE_LOCALE = 'en_US';

/** Paths included in sitemap.xml (no trailing slash). */
export const SITEMAP_ENTRIES = [
  { path: '/product', changefreq: 'weekly', priority: '1.0' },
  { path: '/features', changefreq: 'monthly', priority: '0.9' },
  { path: '/pricing', changefreq: 'weekly', priority: '0.9' },
  { path: '/for-students', changefreq: 'monthly', priority: '0.85' },
  { path: '/for-developers', changefreq: 'monthly', priority: '0.85' },
  { path: '/for-interview-prep', changefreq: 'monthly', priority: '0.85' },
  { path: '/for-teams', changefreq: 'monthly', priority: '0.8' },
  { path: '/ai', changefreq: 'monthly', priority: '0.8' },
  { path: '/security', changefreq: 'monthly', priority: '0.75' },
  { path: '/brand', changefreq: 'monthly', priority: '0.7' },
  { path: '/contact', changefreq: 'yearly', priority: '0.6' },
  { path: '/about', changefreq: 'yearly', priority: '0.6' },
  { path: '/login', changefreq: 'monthly', priority: '0.5' },
  { path: '/privacy', changefreq: 'yearly', priority: '0.4' },
  { path: '/terms', changefreq: 'yearly', priority: '0.4' },
  { path: '/cookies', changefreq: 'yearly', priority: '0.3' },
  { path: '/refund', changefreq: 'yearly', priority: '0.3' },
];

const PRIVATE_PREFIXES = [
  '/dashboard',
  '/hub',
  '/library',
  '/learn',
  '/home',
  '/saved',
  '/practice',
  '/review',
  '/question/',
  '/settings',
  '/notifications',
  '/checkout',
  '/analytics',
  '/leaderboard',
  '/profile',
  '/teams',
  '/admin',
  '/onboarding',
  '/mastery',
  '/tutor',
  '/interview',
  '/skill-gap',
  '/weekly-summary',
  '/auth/callback',
  '/b_upload',
  '/exceptions',
];

export function isPrivateAppRoute(pathname) {
  const path = pathname || '/';
  if (isPublicQuestionPath(path)) return false;
  return PRIVATE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix),
  );
}

export function buildCanonicalUrl(pathname = '/') {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (path === '/') return `${SITE_URL}/product`;
  return `${SITE_URL}${path}`;
}

export function formatDocumentTitle(title) {
  if (!title) return DEFAULT_TITLE;
  if (/qnaread/i.test(title)) return title;
  return `${title} | ${SITE_DOMAIN}`;
}

/** Per-route defaults when pages pass partial PageMeta props. */
export const ROUTE_SEO = {
  '/product': {
    title: 'Product — structured Q&A learning',
    description:
      'Explore QnaRead: platform stats, learner feedback, spaced repetition, AI explain, dashboards, and team workspaces. Free to start.',
    keywords:
      'QnaRead product, Q&A learning platform, study workspace, team learning software',
  },
  '/features': {
    title: 'Product tour — learner journey',
    description:
      'Step-by-step QnaRead journey: Notebook hub & PDF import, Library search, Study hub, save, practice, spaced review, AI explain, analytics, sharing, Teams, and security.',
  },
  '/pricing': {
    title: 'Pricing — Free, Pro & Team',
    description:
      'Compare QnaRead Free, Pro, and Team plans. Stripe checkout for Pro; collaborative workspaces and seat-based Team pricing after sign-in.',
    keywords: 'QnaRead pricing, learning app subscription, team learning plan',
  },
  '/for-students': {
    title: 'For students — exam & course revision',
    description:
      'Structured Q&A notebooks, daily review, streaks, and AI-assisted understanding for syllabus and exam prep on QnaRead.',
  },
  '/for-developers': {
    title: 'For developers — interview & technical prep',
    description:
      'Technical Q&A library, mock interview practice, skill-gap analysis, and spaced repetition for engineering interviews on QnaRead.',
  },
  '/for-interview-prep': {
    title: 'Interview prep with structured Q&A',
    description:
      'Practice technical and behavioral topics with flashcards, AI tutor sessions, and progress tracking on QnaRead.',
  },
  '/for-teams': {
    title: 'For teams — learning workspaces',
    description:
      'Invite members, assignments, challenges, team leaderboards, and manager visibility for company and cohort learning on QnaRead.',
  },
  '/ai': {
    title: 'AI assistant for learning',
    description:
      'Server-side AI explain, mind-map search on Pro, and metered daily limits — built into your QnaRead notebooks, not a generic chatbot.',
  },
  '/security': {
    title: 'Security & privacy',
    description:
      'HttpOnly sessions, optional TOTP MFA, role-based admin, and secure Stripe checkout on QnaRead.',
  },
  '/brand': {
    title: 'QnaRead — learn faster, remember longer',
    description:
      'Brand story for QnaRead — personalized Q&A learning for students, developers, and teams.',
  },
  '/contact': {
    title: 'Contact support',
    description: 'Reach QnaRead support for product questions, billing, and account help.',
  },
  '/login': {
    title: 'Sign in',
    description:
      'Sign in to QnaRead with Google or email. Free account includes notebooks, library search, and practice.',
    keywords: 'QnaRead login, sign in, create account',
  },
  '/about': {
    title: 'About QnaRead',
    description:
      'QnaRead is a learning notebook for developers, students, and professionals who want structured Q&A with AI-assisted revision.',
  },
  '/privacy': { title: 'Privacy policy', description: 'How QnaRead handles account data and learning activity.' },
  '/terms': { title: 'Terms of service', description: 'Terms for using the QnaRead learning platform.' },
  '/cookies': { title: 'Cookie policy', description: 'Cookies and authentication on QnaRead.com.' },
  '/refund': { title: 'Refund policy', description: 'Subscription refund terms for QnaRead paid plans.' },
};

export function getRouteSeoDefaults(pathname) {
  return ROUTE_SEO[pathname] || {};
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: DEFAULT_OG_IMAGE,
      width: OG_IMAGE_WIDTH,
      height: OG_IMAGE_HEIGHT,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: `${SITE_URL}/contact`,
    },
    sameAs: [],
  };
}

export function webSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: SITE_DOMAIN,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: 'en-US',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: DEFAULT_OG_IMAGE,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/login?redirect=${encodeURIComponent('/library')}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/** schema.org QAPage for public /q/:id routes */
export function questionPageJsonLd(item, questionId) {
  if (!item) return null;
  const qText = stripPlain(item.question);
  const aText = stripPlain(item.answer);
  if (!qText || !aText) return null;
  const url = `${SITE_URL}/q/${questionId}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: qText,
      text: qText,
      answerCount: 1,
      acceptedAnswer: {
        '@type': 'Answer',
        text: aText,
        url,
      },
    },
  };
}

function stripPlain(raw) {
  if (raw == null) return '';
  return String(raw).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function softwareApplicationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: `${SITE_URL}/product`,
    description: DEFAULT_DESCRIPTION,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier with optional Pro and Team upgrades',
    },
  };
}
